import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Shield, Heart, LogIn, LogOut, User, Crown, LayoutDashboard, Sun, Moon, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useEventCounter } from "@/hooks/useEventCounter";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/hooks/useTheme";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import PricingDialog from "@/components/PricingDialog";

// Navbar component
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, isPremium, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [pricingOpen, setPricingOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const recentCount = useEventCounter();

  const handleSignIn = () => { setOpen(false); navigate("/auth"); };
  const handleSignOut = async () => { setOpen(false); await signOut(); };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No portal URL returned");
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Could not open subscription manager", variant: "destructive" });
    }
  };

  return (
    <nav className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-md">
      <div className="container mx-auto flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
          <Shield className="h-5 w-5 text-primary" />
          <span className="font-mono text-sm font-bold uppercase tracking-wider text-foreground">GCM</span>
          <span className="hidden text-xs text-muted-foreground sm:inline">Global Conflict Monitor</span>
          {recentCount > 0 && (
            <span className="flex items-center gap-1 rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-[10px] font-bold text-destructive">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-destructive" />
              </span>
              <Activity className="h-2.5 w-2.5" />
              {recentCount}
            </span>
          )}
        </div>

        {/* Desktop */}
        <div className="hidden items-center gap-2 lg:flex">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            {theme === "dark" ? <Sun className="h-3.5 w-3.5 text-muted-foreground" /> : <Moon className="h-3.5 w-3.5 text-muted-foreground" />}
          </Button>

          <PushNotificationToggle />

          {isPremium && (
            <Button variant="outline" size="sm" className="gap-1.5 text-xs border-primary/30 text-primary" onClick={() => navigate("/dashboard")}>
              <LayoutDashboard className="h-3 w-3" /> Dashboard
            </Button>
          )}

          <a href="#donate" onClick={(e) => { e.preventDefault(); document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" }); }} className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10">
            <Heart className="h-3 w-3" /> Donate
          </a>

          {user ? (
            <>
              {isPremium && (
                <span className="flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase text-primary">
                  <Crown className="h-3 w-3" /> Premium
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" /> {user.email?.split("@")[0]}
              </span>
              {isPremium ? (
                <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleManageSubscription}>Manage Plan</Button>
              ) : (
                <Button size="sm" className="text-xs font-semibold" onClick={() => setPricingOpen(true)}>
                  Go Premium
                </Button>
              )}
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleSignOut}>
                <LogOut className="h-3 w-3" /> Sign Out
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleSignIn}>
                <LogIn className="h-3 w-3" /> Sign In
              </Button>
              <Button size="sm" className="text-xs font-semibold" onClick={() => setPricingOpen(true)}>
                Go Premium
              </Button>
            </>
          )}
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden" aria-label={open ? "Close navigation menu" : "Open navigation menu"} aria-expanded={open}>
          {open ? <X className="h-5 w-5 text-foreground" /> : <Menu className="h-5 w-5 text-foreground" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-background p-4 lg:hidden">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
                {theme === "dark" ? <Sun className="h-4 w-4 text-muted-foreground" /> : <Moon className="h-4 w-4 text-muted-foreground" />}
              </Button>
              <span className="text-xs text-muted-foreground">{theme === "dark" ? "Light mode" : "Dark mode"}</span>
              <PushNotificationToggle />
            </div>
            <button onClick={() => { setOpen(false); document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" }); }} className="flex items-center gap-1 text-sm text-destructive">
              <Heart className="h-3 w-3" /> Donate
            </button>
            {isPremium && (
              <Button variant="outline" size="sm" className="gap-1.5 justify-start" onClick={() => { setOpen(false); navigate("/dashboard"); }}>
                <LayoutDashboard className="h-3 w-3" /> Premium Dashboard
              </Button>
            )}
            <hr className="border-border" />
            {user ? (
              <>
                {isPremium && (
                  <span className="flex items-center gap-1 text-xs font-bold uppercase text-primary">
                    <Crown className="h-3 w-3" /> Premium Active
                  </span>
                )}
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <User className="h-3 w-3" /> {user.email?.split("@")[0]}
                </span>
                {isPremium ? (
                  <Button variant="outline" size="sm" onClick={handleManageSubscription}>Manage Plan</Button>
                ) : (
                  <Button size="sm" className="font-semibold" onClick={() => { setOpen(false); setPricingOpen(true); }}>Go Premium</Button>
                )}
                <Button variant="outline" size="sm" className="gap-1.5" onClick={handleSignOut}>
                  <LogOut className="h-3 w-3" /> Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={handleSignIn}>
                  <LogIn className="h-3 w-3" /> Sign In
                </Button>
                <Button size="sm" className="font-semibold" onClick={() => { setOpen(false); setPricingOpen(true); }}>Go Premium</Button>
              </>
            )}
          </div>
        </div>
      )}
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </nav>
  );
};

export default Navbar;
